import React from 'react';
import { WALKTHROUGH_BACK } from '../sandboxCopy';

interface WalkthroughCalloutProps {
  /** e.g. "STEP 1 OF 4 · REVIEW SUBJECTIVE" */
  stepLabel: string;
  title: string;
  /** Paragraphs; newline-containing entries are split further. */
  body?: string | string[];
  footnote?: string;
  cta: string;
  onCta: () => void;
  ctaDisabled?: boolean;
  /** Small helper line under a disabled CTA (e.g. "Select a diagnosis to continue."). */
  ctaDisabledHint?: string;
  /** Back button beside the CTA, returning to the immediately preceding step
   *  (requirement 2026-09-28). Omitted on the first step, where nothing
   *  precedes. */
  onBack?: () => void;
  /** Secondary escape link under a divider at the bottom of the card
   *  (e.g. "Done with Objective findings? Skip to Plan →"). */
  secondary?: string;
  onSecondary?: () => void;
  className?: string;
}

/**
 * The guided-walkthrough card (orange-accented, per the spec screenshots).
 * Desktop renders it as a side panel / floating card next to the content it
 * describes; mobile layouts place it as the single compact instruction box at
 * the top or bottom of the screen — positioning is the caller's job.
 */
const WalkthroughCallout: React.FC<WalkthroughCalloutProps> = ({
  stepLabel,
  title,
  body,
  footnote,
  cta,
  onCta,
  ctaDisabled = false,
  ctaDisabledHint,
  onBack,
  secondary,
  onSecondary,
  className = '',
}) => {
  const paragraphs = (Array.isArray(body) ? body : body ? [body] : []).flatMap(p => p.split('\n'));

  return (
    <div
      className={`rounded-lg border-2 border-orange-400 bg-white dark:bg-dark-gray-800 shadow-lg p-4 sm:p-5 ${className}`}
    >
      {/* Mobile keeps only the bold instruction + one supporting sentence
          (spec §Mobile Walkthrough Requirements): the step label and extra
          paragraphs render from sm upward. */}
      <div className="hidden sm:block text-[11px] font-semibold tracking-wide text-orange-500 uppercase">
        {stepLabel}
      </div>
      <h3 className="sm:mt-1 text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100">
        {title}
      </h3>
      {paragraphs.length > 0 && (
        <div className="mt-2 space-y-2">
          {paragraphs.map((p, i) => (
            <p
              key={i}
              className={`text-sm text-gray-600 dark:text-gray-300 ${i > 0 ? 'hidden sm:block' : ''}`}
            >
              {p}
            </p>
          ))}
        </div>
      )}
      {footnote ? (
        <p className="mt-2 text-sm italic text-gray-500 dark:text-gray-400">{footnote}</p>
      ) : null}
      <div className="mt-4 flex flex-col items-stretch gap-1">
        <div className={`flex items-center gap-2 ${onBack ? 'justify-between' : 'justify-end'}`}>
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-gray-400 hover:text-gray-900 dark:border-dark-gray-600 dark:bg-dark-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
            >
              {WALKTHROUGH_BACK}
            </button>
          ) : null}
          <button
            type="button"
            disabled={ctaDisabled}
            onClick={onCta}
            className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
              ctaDisabled
                ? 'bg-gray-300 dark:bg-dark-gray-600 cursor-not-allowed'
                : 'bg-primary-600 hover:bg-primary-700'
            }`}
          >
            {cta}
          </button>
        </div>
        {ctaDisabled && ctaDisabledHint ? (
          <span className="self-end text-xs text-gray-400">{ctaDisabledHint}</span>
        ) : null}
      </div>
      {secondary && onSecondary ? (
        <div className="mt-3 border-t border-gray-200 pt-3 dark:border-dark-gray-600">
          <button
            type="button"
            onClick={onSecondary}
            className="text-sm font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            {secondary}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default WalkthroughCallout;
